import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { HiArrowLeft } from "react-icons/hi";

/** Renders **bold** segments instead of showing raw asterisks. */
function RichText({ text }) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} className="font-semibold text-ink">{part.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

export default function BlogArticle({ post }) {
  const title = `${post.title} | Chiparama`;
  const image = `https://chiparama.com${post.imageUrl}`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={post.description} />
        <link rel="canonical" href={post.canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:url" content={post.canonicalUrl} />
        <meta property="og:image" content={image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={post.description} />
        <meta name="twitter:image" content={image} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            image,
            author: { "@type": "Organization", name: post.author },
            publisher: {
              "@type": "Organization",
              name: "Chiparama",
              logo: { "@type": "ImageObject", url: "https://chiparama.com/chiparamalogo.jpg" },
            },
            datePublished: "2025-10-02",
            description: post.description,
            mainEntityOfPage: { "@type": "WebPage", "@id": post.canonicalUrl },
          })}
        </script>
      </Helmet>

      <header className="on-dark relative overflow-hidden bg-navy-900 text-white">
        <div className="bg-grid-dark absolute inset-0 [mask-image:linear-gradient(to_bottom,black,transparent)]" aria-hidden="true" />
        <div className="container-x relative max-w-4xl pb-16 pt-[8rem] md:pb-20 md:pt-[10rem]">
          <Link to="/news-blogs" className="mb-8 inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white">
            <HiArrowLeft className="h-4 w-4" aria-hidden="true" /> All insights
          </Link>
          <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-5xl">{post.title}</h1>
          <p className="mt-6 font-mono text-xs uppercase tracking-wider text-slate-400">
            {post.author} · {post.date}
          </p>
        </div>
      </header>

      <article className="bg-white pb-20 md:pb-28">
        <div className="container-x max-w-4xl">
          <img
            src={post.imageUrl}
            alt={post.title}
            width="1200"
            height="675"
            className="relative z-10 -mt-8 aspect-[16/9] w-full rounded-2xl object-cover shadow-lift md:-mt-12"
          />
        </div>
        <div className="container-x mt-12 max-w-3xl">
          {post.content.map((block, i) => {
            if (block.type === "heading")
              return <h2 key={i} className="mb-4 mt-12 text-2xl font-semibold text-ink md:text-3xl">{block.text}</h2>;
            if (block.type === "list")
              return (
                <ul key={i} className="my-6 space-y-4">
                  {block.items.map((item, j) => (
                    <li key={j} className="flex gap-4 text-lg leading-relaxed text-slate-700">
                      <span className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" />
                      <span><RichText text={item} /></span>
                    </li>
                  ))}
                </ul>
              );
            return <p key={i} className="mb-6 text-lg leading-relaxed text-slate-700">{block.text}</p>;
          })}

          <div className="mt-16 rounded-2xl bg-paper p-8 md:p-10">
            <h2 className="text-xl font-semibold text-ink">Ready to discuss your next project?</h2>
            <p className="mt-2 text-slate-600">Tell us the domain, stage and delivery need.</p>
            <Link to="/contact" className="btn-primary mt-6">Discuss your program</Link>
          </div>
        </div>
      </article>
    </>
  );
}
